import { Box } from '@mui/material'
import { FC, MouseEventHandler, Fragment, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { loadingComponent } from '@omnea-app/frontend/components/shared/Loading/loadingComponent'
import { RiskLevelOption, RiskMatrixCell } from '@omnea-app/risks/risksFrontendClient'
import { ListRisksResponse } from '../../../../../../../list/useListRisks'
import { useGetRiskSettings } from '../../../../../../../settings/hooks/useGetRiskSettings'
import { RiskMatrixScoreMainChip } from './components/RiskMatrixScoreMainChip'
import { RiskMatrixScoreScaleChip } from './components/RiskMatrixScoreScaleChip'
import { RiskChipTooltipMetadata } from './types'
import { useRiskMatrixScoreChipLoaded } from './useRiskMatrixScoreChipLoaded'

type Props = {
  probabilityId: string
  impactId: string
  probabilities: RiskLevelOption[]
  impacts: RiskLevelOption[]
  riskMatrixCells: RiskMatrixCell[]
  matrixLevels: RiskLevelOption[]
  hideNameInLabel?: boolean
  onClick?: MouseEventHandler
  showZeroScaleChip: boolean
  labelPrefixType?: 'inherent' | 'residual'
  size?: 'small' | 'medium'
  risksForQuestion?: NonNullable<ListRisksResponse['data']>
  setRiskIdToEdit?: (value: string) => void
} & RiskChipTooltipMetadata

export const RiskMatrixScoreChipLoaded: FC<Props> = loadingComponent(
  ({
    probabilityId,
    impactId,
    impacts,
    probabilities,
    riskMatrixCells,
    matrixLevels,
    hideNameInLabel,
    onClick,
    showZeroScaleChip,
    labelPrefixType,
    size = 'medium',
    risksForQuestion,
    setRiskIdToEdit,
    ...props
  }) => {
    const { t } = useTranslation('risks')
    const riskSettings = useGetRiskSettings().data
    const { tooltip, riskScore, chipLabel, possibleScores, setTooltipOpen } = useRiskMatrixScoreChipLoaded({
      probabilityId,
      impactId,
      probabilities,
      impacts,
      matrixLevels,
      riskMatrixCells,
      hideNameInLabel,
      riskSettings,
      risksForQuestion,
      setRiskIdToEdit,
      ...props,
    })
    const sortedScores = useMemo(() => [...possibleScores].sort((a, b) => a - b), [possibleScores])

    if (!riskScore.label) return null

    const label = labelPrefixType
      ? labelPrefixType === 'inherent'
        ? `${t('chip-tooltip.label.inherent')}: ${chipLabel}`
        : labelPrefixType === 'residual'
          ? `${t('chip-tooltip.label.residual')}: ${chipLabel}`
          : chipLabel
      : chipLabel

    return (
      <Box gap={size === 'medium' ? 1 : 0.5} display="flex" alignItems="center">
        {sortedScores.map((score) =>
          score !== riskScore.score ? (
            <Fragment key={score}>
              {!props.showAggregationMessage && (showZeroScaleChip || score !== 0) && (
                <RiskMatrixScoreScaleChip
                  score={score}
                  possibleScores={sortedScores}
                  size={size === 'medium' ? 'small' : 'tiny'}
                />
              )}
            </Fragment>
          ) : (
            <RiskMatrixScoreMainChip
              possibleScores={sortedScores}
              setTooltipOpen={setTooltipOpen}
              score={score}
              chipLabel={label}
              showAggregationMessage={props.showAggregationMessage}
              onClick={onClick}
              tooltip={tooltip}
              key={score}
              size={size}
            />
          )
        )}
      </Box>
    )
  }
)
